import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import PaymentModal from './PaymentModal';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Textarea } from './ui/textarea';

// Drag and Drop imports
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';

// Import the global auth context
import { useAuth, useCart } from '../App';

// Draggable Player Component (moved outside to avoid unstable nested component)
const DraggablePlayer = ({ booking, teeTime, slotIndex }) => {
  const teeName = teeTime.tee_name || '1st';
  const slotKey = `${teeName}_${teeTime.time}_${slotIndex}`;
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `booking-${booking.id}`,
    data: {
      ...booking,
      tee_time: teeTime.time,
      tee_name: teeName,
      slot_index: slotIndex,
      slotKey: slotKey
    }
  });
  
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`p-3 bg-blue-50 border-2 border-blue-300 border-dashed rounded text-center cursor-grab active:cursor-grabbing ${
        isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''
      }`}
    >
      <div className="text-sm font-medium text-blue-800">
        {booking.first_name || booking.last_name ? 
          `${booking.first_name} ${booking.last_name}`.trim() : 
          'Booked'}
      </div>
      <div className="text-xs text-blue-600 mt-1">
        ↔️ Drag to move
      </div>
    </div>
  );
};

// Droppable Slot Component (moved outside to avoid unstable nested component)
const DroppableSlot = ({ teeTime, slotIndex, children }) => {
  const teeName = teeTime.tee_name || '1st';
  const slotKey = `${teeName}_${teeTime.time}_${slotIndex}`;
  
  const { isOver, setNodeRef } = useDroppable({
    id: `slot-${slotKey}`,
    data: {
      tee_time: teeTime.time,
      tee_name: teeName,
      slot_index: slotIndex,
      slotKey: slotKey
    }
  });
  
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[60px] transition-colors ${
        isOver ? 'bg-green-100 ring-2 ring-green-500 rounded' : ''
      }`}
    >
      {children}
    </div>
  );
};

const TeeSheetView = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { addToCart } = useCart();
  const { toast } = useToast();
  
  const [event, setEvent] = useState(null);
  const [teeTimeslots, setTeeTimeslots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookings, setBookings] = useState({});
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    booking: null
  });
  
  // Cancel own booking state
  const [cancelModal, setCancelModal] = useState({
    isOpen: false,
    booking: null,
    loading: false
  });

  // Orphaned bookings state (bookings that don't match current tee time slots)
  const [orphanedBookings, setOrphanedBookings] = useState([]);
  const [availableTeeTimes, setAvailableTeeTimes] = useState([]);
  const [showOrphanedModal, setShowOrphanedModal] = useState(false);
  const [reassignModal, setReassignModal] = useState({
    isOpen: false,
    booking: null,
    loading: false,
    selectedTime: null,
    selectedSlot: 0
  });

  // Booking form state
  const [bookingData, setBookingData] = useState({
    players_count: 1,
    cart_required: false,
    use_credits: 0,
    use_free_round: false,
    payment_method: 'credit', // credit, free_round, card
    cart_payment_method: 'credit', // 'credit' or 'card' - default to credits
    booking_for_other: false,
    other_user_id: '',
    payment_responsibility: 'booker', // booker, bookee
    player_names: [''],
    // Multi-booking (group payment) - card only
    multi_booking: false,
    additional_user_ids: [], // Up to 3 additional users
    multi_payment_method: 'card', // Payment method for group booking: credit, free_round, card
    // Mhala Hole-in-One Competition
    mhala_hole_in_one: false,
    mhala_hole_in_one_all_players: false, // For multi-booking: enter all players
    mhala_payment_method: 'card', // Separate payment method for Mhala fee: credits, card
    dinner_attending: false, // Prize Giving Dinner
    dinner_partner: false,
    dinner_payment_method: 'credit', // 'credit' or 'card' for the dinner fee
    // Admin-only: "Add lost booking" recovery flow
    lost_booking_payment_method: 'card', // card, credit, free_round, eft_other
    lost_booking_amount: '',
    lost_booking_payfast_ref: '',
    lost_booking_notes: ''
  });

  const [users, setUsers] = useState([]); // For booking for others
  const [cost, setCost] = useState(null);
  const [userSearchOpen, setUserSearchOpen] = useState(false); // For searchable user dropdown
  const [multiUserSearchOpen, setMultiUserSearchOpen] = useState(false); // For multi-booking user search

  // Guest Booking and Reserve Slot State (Admin only)
  const [showGuestBookingModal, setShowGuestBookingModal] = useState(false);
  const [showReserveSlotModal, setShowReserveSlotModal] = useState(false);
  const [guestBookingData, setGuestBookingData] = useState({
    event_id: '',
    tee_time: '',
    tee_name: '1st',
    slot_index: 0,
    guest_name: '',
    guest_phone: '',
    cart_required: false,
    payment_status: 'pending'
  });
  const [reserveSlotData, setReserveSlotData] = useState({
    event_id: '',
    tee_time: '',
    tee_name: '1st',
    slot_index: 0,
    reserve_note: ''
  });

  // Move Mode State (Admin only)
  const [isMoveMode, setIsMoveMode] = useState(false);
  const [pendingMoves, setPendingMoves] = useState([]); // Array of {bookingId, originalPosition, newPosition}
  const [moveHistory, setMoveHistory] = useState([]); // For undo functionality
  const [activeBooking, setActiveBooking] = useState(null); // Currently dragging booking
  const [applyingMoves, setApplyingMoves] = useState(false);

  // Check if event is in the past
  const isPastEvent = event && event.date ? new Date(event.date) < new Date(new Date().toDateString()) : false;

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    fetchEventData();
    fetchUsers(); // Load users for booking for others (all users can now book for others)
  }, [eventId, user]); // Added user dependency

  useEffect(() => {
    if (event && selectedSlot) {
      calculateCost();
    }
  }, [bookingData, selectedSlot, event, users]); // Added users for multi-booking calculations

  const fetchEventData = async () => {
    try {
      const [eventResponse, teeTimes] = await Promise.all([
        axios.get(`/events/${eventId}`),
        axios.get(`/events/${eventId}/tee-times`)
      ]);
      
      setEvent(eventResponse.data);
      
      // Group tee times by tee name and organize for display
      const groupedTimes = groupTeeTimesByTee(teeTimes.data);
      setTeeTimeslots(groupedTimes);
      
      // Fetch existing bookings for this event
      await fetchBookingsForEvent(eventId);
      
      // Fetch orphaned bookings (admin only)
      if (user?.role === 'admin') {
        await fetchOrphanedBookings(eventId);
      }
      
    } catch (error) {
      console.error('Error fetching event data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load event information"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');  // Use general users endpoint
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]); // Ensure users is always an array
    }
  };

  const fetchBookingsForEvent = async (eventId) => {
    try {
      const response = await axios.get(`/events/${eventId}/bookings`);
      const eventBookings = response.data;
      
      // Transform bookings into the format expected by the tee sheet
      const bookingsData = {};
      eventBookings.forEach(booking => {
        const teeTimeKey = booking.tee_time;
        const teeName = booking.tee_name || '1st'; // Default for backward compatibility
        const slotKey = `${teeName}_${teeTimeKey}_${booking.slot_index || 0}`; // Include tee_name for proper isolation
        
        if (!bookingsData[slotKey]) {
          bookingsData[slotKey] = [];
        }
        
        // Handle guest bookings and reserved slots
        let firstName = '';
        let lastName = '';
        
        if (booking.is_guest && booking.guest_name) {
          // Guest booking - use guest_name
          const nameParts = booking.guest_name.split(' ');
          firstName = 'Guest:';
          lastName = booking.guest_name;
        } else if (booking.is_reserved) {
          // Reserved slot
          firstName = '🔒';
          lastName = 'RESERVED';
        } else if (booking.user_name) {
          // Regular booking
          firstName = booking.user_name.split(' ')[0] || '';
          lastName = booking.user_name.split(' ').slice(1).join(' ') || '';
        }
        
        bookingsData[slotKey].push({
          id: booking.id,
          booking_id: booking.id,
          user_id: booking.user_id,
          first_name: firstName,
          last_name: lastName,
          payment_method: booking.payment_method_display || '',
          status_display: booking.status_display || '',
          players_count: booking.players_count,
          booking_reference: booking.booking_reference,
          user_membership: booking.user_membership || 'non-member',
          total_amount: booking.total_amount || 0,
          tee_time: booking.tee_time, // For cancel dialog
          // Guest and Reserved fields
          is_guest: booking.is_guest || false,
          guest_name: booking.guest_name || '',
          guest_phone: booking.guest_phone || '',
          is_reserved: booking.is_reserved || false,
          reserve_note: booking.reserve_note || '',
          // Group booking fields
          group_booking_id: booking.group_booking_id || null,
          payer_id: booking.payer_id || null,
          // Admin-recovery markers (badge on tee sheet for admins)
          admin_recovered: booking.admin_recovered || false,
          admin_recovery_notes: booking.admin_recovery_notes || '',
          payfast_reference: booking.payfast_reference || '',
          // Can cancel - user can cancel if it's their own booking and not already cancelled
          can_cancel: booking.user_id === user?.id && 
                     booking.status !== 'cancelled' && 
                     booking.status_display !== 'Cancelled'
        });
      });
      
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings({});
    }
  };

  const fetchOrphanedBookings = async (eventId) => {
    try {
      const response = await axios.get(`/events/${eventId}/orphaned-bookings`);
      setOrphanedBookings(response.data.orphaned_bookings || []);
      setAvailableTeeTimes(response.data.available_tee_times || []);
    } catch (error) {
      console.error('Error fetching orphaned bookings:', error);
      setOrphanedBookings([]);
      setAvailableTeeTimes([]);
    }
  };

  const handleReassignBooking = async (booking, newTeeTime, newTeeName, newSlotIndex) => {
    setReassignModal(prev => ({ ...prev, loading: true }));
    try {
      await axios.post(`/events/${eventId}/reassign-booking`, {
        booking_id: booking.id,
        new_tee_time: newTeeTime,
        new_tee_name: newTeeName,
        new_slot_index: newSlotIndex
      });
      
      toast({
        title: "Booking Reassigned",
        description: `${booking.user_name} moved to ${new Date(newTeeTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} on ${newTeeName} tee`
      });
      
      // Refresh data
      await fetchEventData();
      setReassignModal({ isOpen: false, booking: null, loading: false });
    } catch (error) {
      console.error('Error reassigning booking:', error);
      toast({
        variant: "destructive",
        title: "Reassignment Failed",
        description: error.response?.data?.detail || "Failed to reassign booking"
      });
      setReassignModal(prev => ({ ...prev, loading: false }));
    }
  };

  const groupTeeTimesByTee = (teeTimes) => {
    const grouped = {};
    
    teeTimes.forEach(slot => {
      const teeName = slot.tee_name || '1st';
      if (!grouped[teeName]) {
        grouped[teeName] = [];
      }
      grouped[teeName].push(slot);
    });

    // Sort times within each tee group
    Object.keys(grouped).forEach(tee => {
      grouped[tee].sort((a, b) => new Date(a.time) - new Date(b.time));
    });

    return grouped;
  };

  const handleSlotClick = (teeTime, slotIndex) => {
    if (teeTime.current_bookings >= teeTime.max_players) {
      toast({
        variant: "destructive",
        title: "Slot Full",
        description: "This tee time is fully booked"
      });
      return;
    }

    setSelectedSlot({ teeTime, slotIndex });
    setShowBookingModal(true);
    
    // Reset booking form with smart default payment method
    const defaultPaymentMethod = (() => {
      // If user has free rounds and event allows them, default to free round
      if (event?.free_rounds_allowed && (user?.free_rounds || 0) > 0) {
        return 'free_round';
      }
      // If user has credits, default to credits
      if ((user?.credits_balance || 0) > 0) {
        return 'credit';
      }
      // Otherwise default to card
      return 'card';
    })();

    setBookingData({
      players_count: 1,
      cart_required: event?.cart_required || false,
      use_credits: 0,
      use_free_round: defaultPaymentMethod === 'free_round',
      payment_method: defaultPaymentMethod,
      cart_payment_method: 'credit', // Default to credits for cart payment
      booking_for_other: false,
      other_user_id: '',
      player_names: [''],
      multi_booking: false,
      additional_user_ids: [],
      multi_payment_method: 'card',
      mhala_hole_in_one: false,
      mhala_hole_in_one_all_players: false,
      dinner_attending: false,
      dinner_partner: false,
      dinner_payment_method: 'credit'
    });
  };

  const calculateCost = async () => {
    if (!selectedSlot) return;
    
    console.log('🔍 calculateCost: Starting calculation...');
    console.log('🔍 calculateCost: bookingData.payment_method:', bookingData.payment_method);
    console.log('🔍 calculateCost: bookingData.use_free_round:', bookingData.use_free_round);
    console.log('🔍 calculateCost: bookingData.multi_booking:', bookingData.multi_booking);
    console.log('🔍 calculateCost: bookingData.additional_user_ids:', bookingData.additional_user_ids);
    console.log('🔍 calculateCost: bookingData.multi_payment_method:', bookingData.multi_payment_method);
    console.log('🔍 calculateCost: bookingData.mhala_hole_in_one:', bookingData.mhala_hole_in_one);
    
    // Mhala Hole-in-One fee calculation
    const mhalaEnabled = event.mhala_hole_in_one_enabled || false;
    const mhalaFeePerPerson = event.mhala_hole_in_one_fee || 50;
    
    // Handle Multi-Booking (group payment)
    if (bookingData.multi_booking && bookingData.additional_user_ids.length > 0) {
      // Check if current user already has a booking
      const userAlreadyBooked = Object.values(bookings).flat().some(
        b => b.user_id === user?.id && b.status !== 'cancelled'
      );
      
      // Build user list - exclude current user if they already have a booking
      const allUserIds = userAlreadyBooked 
        ? [...bookingData.additional_user_ids]
        : [user.id, ...bookingData.additional_user_ids];
      
      let totalGreenFee = 0;
      let totalCartFee = 0;
      let totalMhalaFee = 0;
      let totalDinnerFee = 0;
      const playerBreakdown = [];
      
      for (const userId of allUserIds) {
        const playerUser = userId === user.id ? user : users.find(u => u.id === userId);
        if (!playerUser) continue;
        
        const isMember = playerUser.membership_tier && ['green', 'silver', 'gold', 'special'].includes(playerUser.membership_tier);
        const feePerPlayer = isMember ? event.member_fee : event.non_member_fee;
        const cartFee = event.cart_fee > 0 ? event.cart_fee : 0;
        
        // Mhala fee per player if enabled and opted in
        const playerMhalaFee = (mhalaEnabled && bookingData.mhala_hole_in_one_all_players) ? mhalaFeePerPerson : 0;
        // Prize Giving Dinner is compulsory per player on dinner events
        const playerDinnerFee = event.dinner_enabled ? (event.dinner_player_fee || 0) : 0;
        
        totalGreenFee += feePerPlayer;
        totalCartFee += cartFee;
        totalMhalaFee += playerMhalaFee;
        totalDinnerFee += playerDinnerFee;
        
        playerBreakdown.push({
          name: `${playerUser.first_name} ${playerUser.last_name}`,
          fee: feePerPlayer,
          cartFee: cartFee,
          mhalaFee: playerMhalaFee,
          dinnerFee: playerDinnerFee,
          isMember: isMember
        });
      }
      
      const grandTotal = totalGreenFee + totalCartFee + totalMhalaFee + totalDinnerFee;
      const paymentMethod = bookingData.multi_payment_method || 'card';
      
      let creditsUsed = 0;
      let freeRoundDiscount = 0;
      let finalAmount = grandTotal;
      
      // Calculate based on payment method
      if (paymentMethod === 'free_round' && event.free_rounds_allowed) {
        // Free rounds cover green fee only
        const availableFreeRounds = user?.free_rounds || 0;
        if (availableFreeRounds >= allUserIds.length) {
          freeRoundDiscount = totalGreenFee;
          finalAmount = totalCartFee + totalMhalaFee + totalDinnerFee;  // Cart, Mhala and dinner remain
        }
      } else if (paymentMethod === 'credit') {
        const availableCredits = user?.credits_balance || 0;
        creditsUsed = Math.min(availableCredits, grandTotal);
        finalAmount = grandTotal - creditsUsed;
      }
      
      console.log('🔍 calculateCost: Multi-booking calculation:');
      console.log('  - Players:', allUserIds.length);
      console.log('  - Total Green Fee:', totalGreenFee);
      console.log('  - Total Cart Fee:', totalCartFee);
      console.log('  - Total Mhala Fee:', totalMhalaFee);
      console.log('  - Payment Method:', paymentMethod);
      console.log('  - Credits Used:', creditsUsed);
      console.log('  - Free Round Discount:', freeRoundDiscount);
      console.log('  - Final Amount:', finalAmount);
      
      setCost({
        greenFeeTotal: totalGreenFee,
        cartFeeTotal: totalCartFee,
        mhalaFeeTotal: totalMhalaFee,
        dinnerFeeTotal: totalDinnerFee,
        total: grandTotal,
        creditsUsed: creditsUsed,
        freeRoundDiscount: freeRoundDiscount,
        finalAmount: finalAmount,
        membershipType: 'Group Booking',
        isMultiBooking: true,
        playerCount: allUserIds.length,
        playerBreakdown: playerBreakdown,
        paymentMethod: paymentMethod
      });
      return;
    }
    
    const membershipTier = bookingData.booking_for_other && bookingData.other_user_id ? 
      users.find(u => u.id === bookingData.other_user_id)?.membership_tier : 
      user?.membership_tier;

    // Use member vs non-member pricing (special tier gets member pricing)
    const feePerPlayer = (membershipTier && ['green', 'silver', 'gold', 'special'].includes(membershipTier)) ? 
      event.member_fee : event.non_member_fee;
    
    const greenFeeTotal = feePerPlayer * bookingData.players_count;
    // Cart fee is added if event has a cart fee (carts are always compulsory if cart_fee > 0)
    const cartFeeTotal = event.cart_fee > 0 ? event.cart_fee : 0.0;
    // Mhala fee if enabled and opted in
    const mhalaFeeTotal = (mhalaEnabled && bookingData.mhala_hole_in_one) ? mhalaFeePerPerson : 0;
    // Prize Giving Dinner — compulsory for the player, partner optional
    const dinnerFeeTotal = event.dinner_enabled
      ? (event.dinner_player_fee || 0) + (bookingData.dinner_partner ? (event.dinner_partner_fee || 0) : 0)
      : 0;
    const total = greenFeeTotal + cartFeeTotal + mhalaFeeTotal + dinnerFeeTotal;
    
    let freeRoundDiscount = 0;
    
    // Handle GREEN FEE payment based on selected method
    let greenFeeCreditsUsed = 0;
    
    if (bookingData.payment_method === 'free_round' && event.free_rounds_allowed) {
      // Use FREE ROUND for green fee
      // If booking for other and bookee pays, use target user's resources; otherwise use current user's
      const payingUser = (bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') ?
        users.find(u => u.id === bookingData.other_user_id) : user;
      
      const availableFreeRounds = payingUser?.free_rounds || 0;
      
      if (availableFreeRounds > 0) {
        freeRoundDiscount = greenFeeTotal;
      }
    } else if (bookingData.payment_method === 'credit') {
      // Use CREDITS for green fee
      // If booking for other and bookee pays, use target user's resources; otherwise use current user's
      const payingUser = (bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') ?
        users.find(u => u.id === bookingData.other_user_id) : user;
      
      const availableCredits = payingUser?.credits_balance || 0;
      
      greenFeeCreditsUsed = Math.min(availableCredits, greenFeeTotal);
    }

    // Calculate remaining amount after green fee is handled
    const remainingAmountAfterGreenFee = total - freeRoundDiscount - greenFeeCreditsUsed;

    // Handle CART FEE payment if cart is required
    let cartCreditsUsed = 0;
    if (cartFeeTotal > 0 && bookingData.cart_payment_method === 'credit') {
      // If booking for other and bookee pays, use target user's resources; otherwise use current user's
      const payingUser = (bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') ?
        users.find(u => u.id === bookingData.other_user_id) : user;
      
      const availableCredits = payingUser?.credits_balance || 0;
      
      // Calculate remaining credits after green fee usage
      const remainingCredits = availableCredits - greenFeeCreditsUsed;
      cartCreditsUsed = Math.min(remainingCredits, cartFeeTotal);
    }

    // Handle DINNER FEE payment — player chooses credits or card for the dinner,
    // independent of how the golf is paid (e.g. free round golf + credits dinner)
    let dinnerCreditsUsed = 0;
    if (dinnerFeeTotal > 0 && bookingData.dinner_payment_method === 'credit') {
      const payingUser = (bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') ?
        users.find(u => u.id === bookingData.other_user_id) : user;
      const availableCredits = payingUser?.credits_balance || 0;
      const remainingCredits = availableCredits - greenFeeCreditsUsed - cartCreditsUsed;
      dinnerCreditsUsed = Math.max(0, Math.min(remainingCredits, dinnerFeeTotal));
    }

    // Calculate final amount to pay
    const totalCreditsUsed = greenFeeCreditsUsed + cartCreditsUsed + dinnerCreditsUsed;
    const finalAmount = Math.max(0, remainingAmountAfterGreenFee - cartCreditsUsed - dinnerCreditsUsed);
    
    console.log('🔍 calculateCost: Final calculation results:');
    console.log('  - freeRoundDiscount:', freeRoundDiscount);
    console.log('  - greenFeeCreditsUsed:', greenFeeCreditsUsed);
    console.log('  - cartCreditsUsed:', cartCreditsUsed);
    console.log('  - totalCreditsUsed:', totalCreditsUsed);
    console.log('  - mhalaFeeTotal:', mhalaFeeTotal);
    console.log('  - finalAmount:', finalAmount);
    
    setCost({
      greenFeeTotal,
      cartFeeTotal,
      mhalaFeeTotal,
      dinnerFeeTotal,
      total,
      creditsUsed: totalCreditsUsed,  // Total credits used for both green fee and cart fee
      freeRoundDiscount,
      finalAmount,
      membershipType: (membershipTier && membershipTier !== 'non-member') ? 'Member' : 'Non-Member'
    });
  };

  // Add to Cart handler
  const handleAddToCart = async () => {
    if (!selectedSlot || !event) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a slot first"
      });
      return;
    }

    // Check if user already has a booking for this event
    try {
      const response = await axios.get(`/events/${eventId}/bookings`);
      const eventBookings = response.data;
      const existingBooking = eventBookings.find(booking => 
        booking.user_id === user?.id && 
        booking.status !== 'cancelled'
      );
      
      if (existingBooking) {
        const message = "You have already booked for this event. If your name does not appear on the tee sheet, please notify the organisers.";
        toast({
          variant: "destructive",
          title: "Already Booked",
          description: message,
        });
        alert(`Already Booked: ${message}`);
        return;
      }
    } catch (error) {
      console.error('Error checking for existing bookings:', error);
      // Continue with add to cart - backend will validate
    }

    // Calculate the green fee based on user's membership
    const membershipTier = user?.membership_tier || 'non-member';
    const greenFee = (membershipTier && membershipTier !== 'non-member') 
      ? event.member_fee 
      : event.non_member_fee;
    const cartFee = bookingData.cart_required ? (event.cart_fee || 0) : 0;
    const mhalaFee = event.mhala_hole_in_one_fee || 50;

    // Get the correct tee_time from the slot structure
    const teeTime = selectedSlot.teeTime?.time || selectedSlot.time;
    const teeName = selectedSlot.teeTime?.tee_name || selectedSlot.tee_name || '1st';
    const slotIndex = selectedSlot.slotIndex ?? selectedSlot.slot_index ?? 0;

    const cartItem = {
      event_id: event.id,
      event_name: event.name,
      tee_time: teeTime,
      tee_name: teeName,
      slot_index: slotIndex,
      players_count: bookingData.players_count || 1,
      cart_required: bookingData.cart_required,
      player_names: bookingData.player_names.filter(n => n.trim()),
      mhala_hole_in_one: bookingData.mhala_hole_in_one && event.mhala_hole_in_one_enabled,
      mhala_payment_method: bookingData.mhala_payment_method,
      mhala_fee: mhalaFee,
      dinner_attending: event.dinner_enabled || false,
      dinner_partner: !!(event.dinner_enabled && bookingData.dinner_partner),
      dinner_fee: event.dinner_enabled
        ? (event.dinner_player_fee || 0) + (bookingData.dinner_partner ? (event.dinner_partner_fee || 0) : 0)
        : 0,
      green_fee: greenFee,
      cart_fee: cartFee,
      membership_tier: membershipTier
    };

    const result = addToCart(cartItem);
    
    if (result.success) {
      toast({
        title: "Added to Cart",
        description: `${event.name} - ${teeTime ? new Date(teeTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''} added to your cart`
      });
      setShowBookingModal(false);
    } else {
      toast({
        variant: "destructive",
        title: "Already in Cart",
        description: result.message
      });
    }
  };

  const handleBooking = async () => {
    console.log('🔍 handleBooking: Starting booking process...');
    console.log('🔍 handleBooking: Selected slot:', selectedSlot);
    console.log('🔍 handleBooking: Booking data:', bookingData);
    console.log('🔍 handleBooking: Cost data:', cost);
    console.log('🔍 handleBooking: Booking for other?', bookingData.booking_for_other);
    console.log('🔍 handleBooking: Other user ID:', bookingData.other_user_id);
    console.log('🔍 handleBooking: Payment responsibility:', bookingData.payment_responsibility);
    console.log('🔍 handleBooking: Users list length:', users.length);
    
    if (!selectedSlot) {
      console.log('❌ handleBooking: No selected slot, returning');
      return;
    }

    // Admin "Add lost booking" recovery flow: bypass normal booking path entirely
    if (
      user?.role === 'admin' &&
      bookingData.booking_for_other &&
      bookingData.payment_responsibility === 'lost_booking'
    ) {
      if (!bookingData.other_user_id) {
        toast({ variant: 'destructive', title: 'Select user', description: 'Please select the user this lost booking belongs to.' });
        return;
      }
      try {
        const payload = {
          event_id: eventId,
          tee_time: selectedSlot.teeTime.time,
          tee_name: selectedSlot.teeTime.tee_name || '1st',
          slot_index: selectedSlot.slotIndex,
          user_id: bookingData.other_user_id,
          payment_method: bookingData.lost_booking_payment_method || 'card',
          amount: parseFloat(bookingData.lost_booking_amount) || 0,
          payfast_reference: bookingData.lost_booking_payfast_ref || null,
          notes: bookingData.lost_booking_notes || null,
          cart_required: !!event?.cart_fee && event.cart_fee > 0,
          cart_fee_paid: 0
        };
        const resp = await axios.post('/admin/bookings/recover-lost', payload);
        toast({ title: 'Lost booking recovered', description: `Reference: ${resp.data.booking_reference}` });
        setShowBookingModal(false);
        fetchEventData();
      } catch (err) {
        toast({
          variant: 'destructive',
          title: 'Recovery failed',
          description: err.response?.data?.detail || 'Could not recover lost booking'
        });
      }
      return;
    }

    // Additional validation for booking for others
    if (bookingData.booking_for_other) {
      if (!bookingData.other_user_id) {
        console.log('❌ handleBooking: Booking for other but no user selected');
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Please select a user to book for"
        });
        return;
      }
      console.log('✅ handleBooking: Validation passed for booking for others');
    }

    // Check for duplicate booking before submission
    const checkDuplicateBooking = async () => {
      // Skip duplicate check for current user if doing multi-booking
      // (we handle this separately in multi-booking flow)
      if (bookingData.multi_booking && bookingData.additional_user_ids.length > 0) {
        // For multi-booking, we'll check duplicates in the multi-booking flow itself
        return false;
      }
      
      let userToCheck;
      let userName;
      
      if (bookingData.booking_for_other) {
        // Booking for someone else - check the target user
        const targetUser = users.find(u => u.id === bookingData.other_user_id);
        userToCheck = bookingData.other_user_id;
        userName = targetUser ? `${targetUser.first_name} ${targetUser.last_name}` : 'Selected user';
      } else {
        // Booking for self
        userToCheck = user?.id;
        userName = user ? `${user.first_name} ${user.last_name}` : 'You';
      }
      
      try {
        // Fetch fresh bookings data to check for duplicates
        const response = await axios.get(`/events/${eventId}/bookings`);
        const eventBookings = response.data;
        
        // Check if this user already has a booking for this event
        const existingBooking = eventBookings.find(booking => 
          booking.user_id === userToCheck && 
          booking.status !== 'cancelled'
        );
        
        if (existingBooking) {
          console.log('🚨 Duplicate booking found for user:', userName);
          console.log('🚨 Existing booking details:', existingBooking);
          console.log('🚨 Attempting to show toast message...');
          
          const message = `${userName} ${bookingData.booking_for_other ? 'has' : 'have'} already booked for this event. If ${bookingData.booking_for_other ? 'their' : 'your'} name does not appear on the tee sheet, please notify the organisers.`;
          
          // Try toast first
          toast({
            variant: "destructive",
            title: "Already Booked",
            description: message,
          });
          
          // Also show browser alert as fallback to ensure user sees the message
          alert(`Already Booked: ${message}`);
          
          console.log('🚨 Toast message sent');
          return true; // Duplicate found
        }
        
        return false; // No duplicate found
      } catch (error) {
        console.error('Error checking for duplicate bookings:', error);
        // If we can't check for duplicates, allow the booking to proceed
        // The backend will handle any duplicate validation
        return false;
      }
    };
    
    // Perform duplicate booking check
    if (await checkDuplicateBooking()) {
      console.log('❌ handleBooking: Duplicate booking detected, aborting');
      return;
    }

    try {
      // Handle Multi-Booking (group payment)
      if (bookingData.multi_booking && bookingData.additional_user_ids.length > 0) {
        console.log('🔍 handleBooking: Multi-booking mode with', bookingData.additional_user_ids.length, 'additional users');
        console.log('🔍 handleBooking: Payment method:', bookingData.multi_payment_method);
        
        try {
          // Check if current user already has a booking for this event
          const eventBookingsResponse = await axios.get(`/events/${eventId}/bookings`);
          const eventBookings = eventBookingsResponse.data;
          const userAlreadyBooked = eventBookings.some(b => b.user_id === user?.id && b.status !== 'cancelled');
          
          // Build user_ids list - exclude current user if they already have a booking
          let userIds;
          if (userAlreadyBooked) {
            console.log('🔍 handleBooking: Current user already booked, only booking additional users');
            userIds = [...bookingData.additional_user_ids];
          } else {
            userIds = [user.id, ...bookingData.additional_user_ids];
          }
          
          // Check slots availability for all users
          const totalPlayers = userIds.length;
          const startSlot = selectedSlot.slotIndex;
          
          if (startSlot + totalPlayers > 4) {
            toast({
              variant: "destructive",
              title: "Not Enough Slots",
              description: `Need ${totalPlayers} consecutive slots but only ${4 - startSlot} available from this position.`
            });
            return;
          }
          
          const multiBookingRequest = {
            event_id: eventId,
            tee_time: selectedSlot.teeTime.time,
            tee_name: selectedSlot.teeTime.tee_name || '1st',
            starting_slot_index: startSlot,
            user_ids: userIds,
            cart_required: event.cart_fee > 0,
            payment_method: bookingData.multi_payment_method || 'card',
            mhala_hole_in_one_user_ids: bookingData.mhala_hole_in_one_all_players ? userIds : []
          };
          
          console.log('🔍 handleBooking: Multi-booking request:', multiBookingRequest);
          
          const response = await axios.post('/bookings/multi', multiBookingRequest);
          
          console.log('✅ handleBooking: Multi-booking successful:', response.data);
          
          // Check if payment is required (card payment)
          if (response.data.payment_form_data && response.data.payment_form_action) {
            toast({
              title: "Group Booking Created!",
              description: `Booking ${response.data.bookings.length} players. Redirecting to PayFast...`
            });
            
            localStorage.setItem('pending_group_booking_id', response.data.group_booking_id);
            
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = response.data.payment_form_action;
            
            Object.keys(response.data.payment_form_data).forEach(key => {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = key;
              input.value = response.data.payment_form_data[key];
              form.appendChild(input);
            });
            
            document.body.appendChild(form);
            form.submit();
            return;
          }
          
          // Payment completed (credits or free rounds)
          toast({
            title: "Group Booking Confirmed!",
            description: response.data.message || `Booked ${response.data.bookings.length} players successfully`
          });
          
          setShowBookingModal(false);
          fetchEventData();
          refreshUser();
          return;
          
        } catch (multiBookingError) {
          console.error('❌ handleBooking: Multi-booking error:', multiBookingError);
          console.error('❌ handleBooking: Error response:', multiBookingError.response?.data);
          
          toast({
            variant: "destructive",
            title: "Group Booking Failed",
            description: multiBookingError.response?.data?.detail || "Failed to create group booking. Please try again."
          });
          return;
        }
      }
      
      // Cart is required if event has a cart fee (carts are always compulsory)
      const cartIsRequired = event.cart_fee > 0;
      
      const bookingRequest = {
        event_id: eventId,
        tee_time: selectedSlot.teeTime.time,
        tee_name: selectedSlot.teeTime.tee_name || '1st',  // Include which tee this booking is for
        slot_index: selectedSlot.slotIndex,  // Include the specific slot being booked
        players_count: bookingData.players_count,
        cart_required: cartIsRequired,
        use_credits: cost?.creditsUsed || 0,
        use_free_round: (bookingData.payment_method === 'free_round' || bookingData.use_free_round) && event.free_rounds_allowed,
        player_names: bookingData.player_names.filter(name => name.trim()),
        booking_for_user_id: bookingData.booking_for_other ? bookingData.other_user_id : undefined,
        payment_responsibility: bookingData.booking_for_other ? bookingData.payment_responsibility : 'booker',
        cart_payment_method: cartIsRequired ? bookingData.cart_payment_method : null,  // Only send if cart is being used
        payment_method: bookingData.payment_method,
        mhala_hole_in_one: bookingData.mhala_hole_in_one && event.mhala_hole_in_one_enabled,
        mhala_payment_method: bookingData.mhala_hole_in_one && event.mhala_hole_in_one_enabled ? bookingData.mhala_payment_method : null,
        dinner_attending: event.dinner_enabled || false,
        dinner_partner: !!(event.dinner_enabled && bookingData.dinner_partner)
      };

      console.log('🔍 handleBooking: Booking request:', bookingRequest);
      
      const response = await axios.post('/bookings', bookingRequest);
      
      console.log('✅ handleBooking: Booking successful:', response.data);
      
      // Check if card payment requires PayFast form submission
      if (response.data.payment_form_data && response.data.payment_form_action) {
        console.log('🔄 handleBooking: Submitting PayFast form for payment');
        
        toast({
          title: "Booking Created!",
          description: "Redirecting to PayFast for payment..."
        });
        
        // Store booking info for potential return
        localStorage.setItem('pending_booking_reference', response.data.booking_reference);
        
        // Create and submit POST form to PayFast
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.data.payment_form_action;
        
        // Add all payment data as hidden form fields
        Object.keys(response.data.payment_form_data).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = response.data.payment_form_data[key];
          form.appendChild(input);
        });
        
        // Submit form to PayFast
        document.body.appendChild(form);
        form.submit();
        return; // Don't continue with normal flow
      }
      
      // Normal booking completion (no card payment required)
      if (response.data.status === 'pending') {
        // Pending without a PayFast form = bookee pays later (or payment gateway
        // unavailable). Never claim "confirmed" for a pending booking.
        toast({
          title: "Booking Created — Payment Pending",
          description: `Booking reference: ${response.data.booking_reference}. Payment is still required to confirm this booking.`
        });
      } else {
        toast({
          title: "Booking Confirmed!",
          description: `Booking reference: ${response.data.booking_reference}`
        });
      }
      
      setShowBookingModal(false);
      fetchEventData(); // Refresh the tee sheet
      refreshUser(); // Refresh user data to update credits balance
      
    } catch (error) {
      console.error('❌ handleBooking: Booking failed:', error);
      console.error('❌ handleBooking: Error response:', error.response?.data);
      
      toast({
        variant: "destructive",
        title: "Booking Failed",
        description: error.response?.data?.detail || "Failed to create booking"
      });
    }
  };

  const handlePayNow = (booking) => {
    setPaymentModal({
      isOpen: true,
      booking: booking
    });
  };

  // Handle cancel own booking from tee sheet
  const handleCancelOwnBooking = (booking) => {
    setCancelModal({
      isOpen: true,
      booking: booking,
      loading: false
    });
  };

  const confirmCancelOwnBooking = async () => {
    if (!cancelModal.booking) return;
    
    setCancelModal(prev => ({ ...prev, loading: true }));
    
    try {
      await axios.delete(`/bookings/${cancelModal.booking.booking_id}`);
      
      toast({
        title: "Booking Cancelled",
        description: `Your booking has been cancelled successfully.`
      });
      
      setCancelModal({ isOpen: false, booking: null, loading: false });
      fetchEventData(); // Refresh tee sheet
      if (refreshUser) refreshUser(); // Refresh user to update credits/free rounds if refunded
    } catch (error) {
      console.error('Cancel error:', error);
      toast({
        variant: "destructive",
        title: "Cancel Failed",
        description: error.response?.data?.detail || "Failed to cancel booking"
      });
      setCancelModal(prev => ({ ...prev, loading: false }));
    }
  };

  // ====== GUEST BOOKING & RESERVE SLOT FUNCTIONS (Admin Only) ======
  
  const handleGuestBooking = async () => {
    try {
      if (!guestBookingData.guest_name.trim()) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Guest name is required"
        });
        return;
      }
      
      await axios.post('/admin/bookings/guest', guestBookingData);
      
      toast({
        title: "Guest Booking Created",
        description: `Booking created for ${guestBookingData.guest_name}`
      });
      
      setShowGuestBookingModal(false);
      setGuestBookingData({
        event_id: '',
        tee_time: '',
        tee_name: '1st',
        slot_index: 0,
        guest_name: '',
        guest_phone: '',
        cart_required: false,
        payment_status: 'pending'
      });
      
      fetchEventData(); // Refresh tee sheet
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to create guest booking"
      });
    }
  };

  const handleReserveSlot = async () => {
    try {
      await axios.post('/admin/bookings/reserve', reserveSlotData);
      
      toast({
        title: "Slot Reserved",
        description: "Slot has been reserved successfully"
      });
      
      setShowReserveSlotModal(false);
      setReserveSlotData({
        event_id: '',
        tee_time: '',
        tee_name: '1st',
        slot_index: 0,
        reserve_note: ''
      });
      
      fetchEventData(); // Refresh tee sheet
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to reserve slot"
      });
    }
  };

  const handleReleaseReservation = async (bookingId) => {
    try {
      await axios.post(`/admin/bookings/release-reservation/${bookingId}`);
      
      toast({
        title: "Reservation Released",
        description: "Slot is now available for booking"
      });
      
      fetchEventData(); // Refresh tee sheet
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to release reservation"
      });
    }
  };

  // ====== MOVE MODE FUNCTIONS (Admin Only) ======
  
  const enterMoveMode = () => {
    setIsMoveMode(true);
    setPendingMoves([]);
    setMoveHistory([]);
    toast({
      title: "Move Mode Activated",
      description: "Drag players to move them. Click 'Apply Changes' when done."
    });
  };

  const exitMoveMode = () => {
    setIsMoveMode(false);
    setPendingMoves([]);
    setMoveHistory([]);
    setActiveBooking(null);
    toast({
      title: "Move Mode Cancelled",
      description: "All pending changes have been discarded."
    });
  };

  const undoLastMove = () => {
    if (moveHistory.length === 0) return;
    
    const lastMove = moveHistory[moveHistory.length - 1];
    
    // Remove from pending moves
    setPendingMoves(prev => prev.filter(m => m.bookingId !== lastMove.bookingId));
    
    // Remove from history
    setMoveHistory(prev => prev.slice(0, -1));
    
    toast({
      title: "Move Undone",
      description: `Restored ${lastMove.playerName} to original position.`
    });
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const bookingData = active.data.current;
    setActiveBooking(bookingData);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveBooking(null);
    
    if (!over || !active) return;
    
    const sourceBooking = active.data.current;
    const targetSlot = over.data.current;
    
    // If dropped on the same slot, do nothing
    if (sourceBooking.slotKey === targetSlot.slotKey) return;
    
    // Check if target slot has a booking (swap scenario)
    const targetBooking = bookings[targetSlot.slotKey]?.[0];
    
    // Record the move(s)
    const newMoves = [];
    
    // Move the source booking to target
    newMoves.push({
      bookingId: sourceBooking.id,
      playerName: `${sourceBooking.first_name} ${sourceBooking.last_name}`.trim() || 'Player',
      originalPosition: {
        tee_time: sourceBooking.tee_time,
        tee_name: sourceBooking.tee_name,
        slot_index: sourceBooking.slot_index,
        slotKey: sourceBooking.slotKey
      },
      newPosition: {
        tee_time: targetSlot.tee_time,
        tee_name: targetSlot.tee_name,
        slot_index: targetSlot.slot_index,
        slotKey: targetSlot.slotKey
      }
    });
    
    // If swapping, also move the target booking to source
    if (targetBooking) {
      newMoves.push({
        bookingId: targetBooking.id,
        playerName: `${targetBooking.first_name} ${targetBooking.last_name}`.trim() || 'Player',
        originalPosition: {
          tee_time: targetBooking.tee_time,
          tee_name: targetBooking.tee_name,
          slot_index: targetBooking.slot_index,
          slotKey: targetSlot.slotKey
        },
        newPosition: {
          tee_time: sourceBooking.tee_time,
          tee_name: sourceBooking.tee_name,
          slot_index: sourceBooking.slot_index,
          slotKey: sourceBooking.slotKey
        }
      });
    }
    
    // Update pending moves (replace any existing moves for these bookings)
    setPendingMoves(prev => {
      const filtered = prev.filter(m => 
        !newMoves.some(nm => nm.bookingId === m.bookingId)
      );
      return [...filtered, ...newMoves];
    });
    
    // Add to history for undo
    setMoveHistory(prev => [...prev, ...newMoves]);
    
    // Update local bookings state for visual preview
    const updatedBookings = { ...bookings };
    
    // Remove from original positions
    newMoves.forEach(move => {
      if (updatedBookings[move.originalPosition.slotKey]) {
        updatedBookings[move.originalPosition.slotKey] = updatedBookings[move.originalPosition.slotKey]
          .filter(b => b.id !== move.bookingId);
        if (updatedBookings[move.originalPosition.slotKey].length === 0) {
          delete updatedBookings[move.originalPosition.slotKey];
        }
      }
    });
    
    // Add to new positions
    newMoves.forEach(move => {
      const bookingToMove = Object.values(bookings).flat().find(b => b.id === move.bookingId);
      if (bookingToMove) {
        const updatedBooking = {
          ...bookingToMove,
          tee_time: move.newPosition.tee_time,
          tee_name: move.newPosition.tee_name,
          slot_index: move.newPosition.slot_index
        };
        updatedBookings[move.newPosition.slotKey] = [updatedBooking];
      }
    });
    
    setBookings(updatedBookings);
    
    const moveType = targetBooking ? 'swapped' : 'moved';
    toast({
      title: targetBooking ? "Players Swapped" : "Player Moved",
      description: `${sourceBooking.first_name} ${moveType}. Click 'Apply Changes' to save.`
    });
  };

  const applyMoves = async () => {
    if (pendingMoves.length === 0) {
      toast({
        variant: "destructive",
        title: "No Changes",
        description: "No moves to apply."
      });
      return;
    }
    
    setApplyingMoves(true);
    
    try {
      const movesPayload = pendingMoves.map(move => ({
        booking_id: move.bookingId,
        new_tee_time: move.newPosition.tee_time,
        new_tee_name: move.newPosition.tee_name,
        new_slot_index: move.newPosition.slot_index
      }));
      
      const response = await axios.post('/admin/bookings/move-players', {
        event_id: eventId,
        moves: movesPayload
      });
      
      if (response.data.success) {
        toast({
          title: "Changes Applied!",
          description: response.data.message
        });
        
        // Exit move mode and refresh data
        setIsMoveMode(false);
        setPendingMoves([]);
        setMoveHistory([]);
        fetchEventData(); // Refresh to get the actual state from server
      } else {
        toast({
          variant: "destructive",
          title: "Some Moves Failed",
          description: response.data.message
        });
        // Refresh to get actual state
        fetchEventData();
      }
    } catch (error) {
      console.error('Error applying moves:', error);
      toast({
        variant: "destructive",
        title: "Failed to Apply Changes",
        description: error.response?.data?.detail || "An error occurred while saving changes."
      });
      // Refresh to restore actual state
      fetchEventData();
    } finally {
      setApplyingMoves(false);
    }
  };

  // ====== END MOVE MODE FUNCTIONS ======

  const renderPlayerSlot = (teeTime, slotIndex) => {
    const teeName = teeTime.tee_name || '1st'; // Get tee name from teeTime object
    const bookedPlayers = bookings[`${teeName}_${teeTime.time}_${slotIndex}`];
    const isBooked = bookedPlayers && bookedPlayers.length > 0;
    const isAvailable = teeTime.current_bookings < teeTime.max_players && event?.bookings_open;

    // In Move Mode - show draggable players for admin
    if (isMoveMode && user?.role === 'admin') {
      if (isBooked) {
        const booking = bookedPlayers[0];
        return (
          <DroppableSlot teeTime={teeTime} slotIndex={slotIndex}>
            <DraggablePlayer booking={booking} teeTime={teeTime} slotIndex={slotIndex} />
          </DroppableSlot>
        );
      } else {
        // Empty slot - can be a drop target
        return (
          <DroppableSlot teeTime={teeTime} slotIndex={slotIndex}>
            <div className="p-3 bg-gray-100 border-2 border-gray-300 border-dashed rounded text-center min-h-[60px] flex items-center justify-center">
              <span className="text-xs text-gray-500">Drop here</span>
            </div>
          </DroppableSlot>
        );
      }
    }

    // Normal mode (non-move mode)
    if (isBooked) {
      const booking = bookedPlayers[0];
      return (
        <div className="p-3 bg-green-50 border border-green-200 rounded text-center">
          <div className="text-sm font-medium text-green-800">
            {booking.first_name || booking.last_name ? 
              `${booking.first_name} ${booking.last_name}`.trim() : 
              'Booked'}
          </div>
          {user?.role === 'admin' ? (
            <div className="space-y-1 mt-1">
              <div className={`text-xs px-2 py-1 rounded ${
                booking.status_display === 'Confirmed' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {booking.status_display}
              </div>
              <div className="text-xs text-green-600">
                {booking.payment_method}
              </div>
              {booking.total_amount > 0 && (
                <div className="text-xs text-gray-600">
                  R{booking.total_amount.toFixed(2)}
                </div>
              )}
              <div className="text-xs text-blue-600 capitalize">
                {booking.user_membership}
              </div>
              {booking.admin_recovered && (
                <div
                  className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                  title={booking.admin_recovery_notes || 'Recovered by admin'}
                  data-testid={`admin-recovered-badge-${booking.id || booking.booking_id || ''}`}
                >
                  admin recovered
                </div>
              )}
              {/* Pay Now button for pending payments */}
              {booking.status_display === 'Pending Payment' && booking.total_amount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 w-full text-xs py-1 h-auto bg-orange-50 hover:bg-orange-100 border-orange-200"
                  onClick={() => handlePayNow({
                    id: booking.booking_id,
                    booking_reference: booking.booking_reference,
                    total_amount: booking.total_amount,
                    event_name: event?.name,
                    tee_time: `${booking.tee_time}`,
                    players_count: booking.players_count
                  })}
                >
                  💳 Pay Now
                </Button>
              )}
            </div>
          ) : (
            // Regular users see their own booking options
            <>
              {/* Show options for user's own bookings */}
              {booking.user_id === user?.id && booking.status_display !== 'Cancelled' && (
                <div className="mt-1 space-y-1">
                  {/* Pay Now for pending bookings */}
                  {booking.status_display === 'Pending Payment' && booking.total_amount > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-xs py-1 h-auto bg-orange-50 hover:bg-orange-100 border-orange-200"
                      onClick={() => handlePayNow({
                        id: booking.booking_id,
                        booking_reference: booking.booking_reference,
                        total_amount: booking.total_amount,
                        event_name: event?.name,
                        tee_time: `${booking.tee_time}`,
                        players_count: booking.players_count
                      })}
                    >
                      💳 Pay Now
                    </Button>
                  )}
                  {/* Cancel button for user's own booking */}
                  {booking.can_cancel && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-xs py-1 h-auto bg-red-50 hover:bg-red-100 border-red-200 text-red-700"
                      onClick={() => handleCancelOwnBooking(booking)}
                      data-testid={`cancel-own-booking-${booking.booking_id}`}
                    >
                      ❌ Cancel
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    // For past events, show empty slot indicator (no booking button)
    if (isPastEvent) {
      return (
        <div className="p-3 bg-gray-100 border border-gray-200 rounded text-center">
          <span className="text-xs text-gray-400">Empty</span>
        </div>
      );
    }

    // For admins, show additional options (Guest, Reserve)
    if (user?.role === 'admin' && isAvailable) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="default"
              size="sm"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid={`book-slot-${teeTime.time}-${slotIndex}`}
            >
              Book Slot ▾
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center">
            <DropdownMenuItem onClick={() => handleSlotClick(teeTime, slotIndex)}>
              📝 Book for User
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              setGuestBookingData({
                event_id: eventId,
                tee_time: teeTime.time,
                tee_name: teeTime.tee_name || '1st',
                slot_index: slotIndex
              });
              setShowGuestBookingModal(true);
            }}>
              👤 Guest Booking
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              setReserveSlotData({
                event_id: eventId,
                tee_time: teeTime.time,
                tee_name: teeTime.tee_name || '1st',
                slot_index: slotIndex
              });
              setShowReserveSlotModal(true);
            }}>
              🔒 Reserve Slot
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <Button
        variant="default"
        size="sm"
        className={`w-full ${
          !event?.bookings_open 
            ? 'bg-gray-400 text-gray-700 cursor-not-allowed hover:bg-gray-400' 
            : !isAvailable 
            ? 'bg-gray-300 text-gray-600 cursor-not-allowed hover:bg-gray-300'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
        onClick={() => handleSlotClick(teeTime, slotIndex)}
        disabled={!isAvailable}
        data-testid={`book-slot-${teeTime.time}-${slotIndex}`}
      >
        {!event?.bookings_open ? 'Bookings Closed' : 'Book Now'}
      </Button>
    );
  };

  // Export tee sheet to CSV
  const exportTeeSheet = () => {
    try {
      let csvContent = `Tee Sheet Export - ${event.name}\n`;
      csvContent += `Date: ${new Date(event.date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}\n\n`;

      // Loop through each tee section
      const isShotgun = !!event?.shotgun_start;
      const sections = isShotgun
        ? [['Shotgun', getShotgunCombinedSlots()]]
        : Object.entries(teeTimeslots);

      sections.forEach(([teeName, teeSlots]) => {
        if (isShotgun) {
          const startTime = teeSlots[0]?.time
            ? new Date(teeSlots[0].time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false })
            : '';
          csvContent += `SHOTGUN START${startTime ? ` - ${startTime}` : ''}\n`;
          csvContent += 'Hole,Player 1,Player 2,Player 3,Player 4\n';
        } else {
          csvContent += `${teeName.toUpperCase()} TEE\n`;
          csvContent += 'Time,Player 1,Player 2,Player 3,Player 4\n';
        }

        teeSlots.forEach(teeTime => {
          const firstCol = isShotgun
            ? `Hole ${teeTime.hole_number ?? (teeTime.tee_name || '').replace(/[^0-9]/g, '')}`
            : new Date(teeTime.time).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: false
              });

          const players = [];
          for (let i = 0; i < 4; i++) {
            // Use the correct key format: teeName_teeTime.time_slotIndex
            const slotKey = `${teeTime.tee_name || teeName}_${teeTime.time}_${i}`;
            const bookedPlayers = bookings[slotKey];

            if (bookedPlayers && bookedPlayers.length > 0) {
              const booking = bookedPlayers[0];
              // Get player name (first_name + last_name) in UPPERCASE
              const playerName = `${booking.first_name || ''} ${booking.last_name || ''}`.trim().toUpperCase();
              players.push(playerName || 'BOOKED');
            } else {
              players.push('');
            }
          }

          csvContent += `${firstCol},${players.join(',')}\n`;
        });

        csvContent += '\n'; // Empty line between tee sections
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `tee_sheet_${event.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Export Successful",
        description: "Tee sheet has been exported to CSV",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export tee sheet",
        variant: "destructive",
      });
    }
  };

  const renderTeeSection = (teeName, teeSlots, isShotgun = false) => {
    // For shotgun events, the heading shows the common start time; for regular events it shows the tee name.
    const sectionHeader = isShotgun
      ? `SHOTGUN START${teeSlots[0]?.time ? ` - ${new Date(teeSlots[0].time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false })}` : ''}`
      : `${teeName.toUpperCase()} TEE`;
    const firstColLabel = isShotgun ? 'Hole' : 'Time';

    return (
      <div key={teeName} className="mb-8">
        <div className="bg-gray-800 text-white p-3 font-bold text-center mb-4 rounded-t">
          {sectionHeader}
        </div>

        <div className="bg-white border rounded-b shadow-sm">
          {/* Mobile-friendly table wrapper with horizontal scroll */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="p-2 sm:p-3 text-left font-semibold text-gray-700 w-24 sm:w-32 text-xs sm:text-sm">{firstColLabel}</th>
                  <th className="p-2 sm:p-3 text-center font-semibold text-gray-700 text-xs sm:text-sm">Player 1</th>
                  <th className="p-2 sm:p-3 text-center font-semibold text-gray-700 text-xs sm:text-sm">Player 2</th>
                  <th className="p-2 sm:p-3 text-center font-semibold text-gray-700 text-xs sm:text-sm">Player 3</th>
                  <th className="p-2 sm:p-3 text-center font-semibold text-gray-700 text-xs sm:text-sm">Player 4</th>
                </tr>
              </thead>
              <tbody>
                {teeSlots.map((teeTime, index) => (
                  <tr key={teeTime.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-2 sm:p-3 font-medium text-gray-900 text-xs sm:text-sm">
                      {isShotgun
                        ? `Hole ${teeTime.hole_number ?? (teeTime.tee_name || '').replace(/[^0-9]/g, '')}`
                        : new Date(teeTime.time).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: false
                          })}
                    </td>
                    <td className="p-1 sm:p-2">{renderPlayerSlot(teeTime, 0)}</td>
                    <td className="p-1 sm:p-2">{renderPlayerSlot(teeTime, 1)}</td>
                    <td className="p-1 sm:p-2">{renderPlayerSlot(teeTime, 2)}</td>
                    <td className="p-1 sm:p-2">{renderPlayerSlot(teeTime, 3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // For shotgun events, flatten all "Hole N" sections into a single sorted list
  // so the tee sheet shows one combined table with hole rows instead of separate sections.
  const getShotgunCombinedSlots = () => {
    const all = [];
    Object.values(teeTimeslots).forEach(slots => {
      slots.forEach(s => all.push(s));
    });
    all.sort((a, b) => {
      const ah = a.hole_number ?? parseInt((a.tee_name || '').replace(/[^0-9]/g, '') || '0', 10);
      const bh = b.hole_number ?? parseInt((b.tee_name || '').replace(/[^0-9]/g, '') || '0', 10);
      return ah - bh;
    });
    return all;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tee sheet...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Alert className="max-w-md">
          <AlertDescription>Event not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Event Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="event-name">
                {event.name}
              </h1>
              <p className="text-gray-600 mt-2">
                {new Date(event.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              {event.description && (
                <p className="text-gray-700 mt-3 max-w-3xl">{event.description}</p>
              )}
              {isPastEvent && (
                <div className="mt-4">
                  <Badge className="bg-gray-500 text-white text-sm px-3 py-1">
                    📅 Past Event - View Only
                  </Badge>
                </div>
              )}
              {!event.bookings_open && !isPastEvent && (
                <div className="mt-4">
                  <Badge className="bg-red-100 text-red-800 text-sm px-3 py-1">
                    🚫 Bookings Closed for this Event
                  </Badge>
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {user && user.role === 'admin' && !isMoveMode && !isPastEvent && (
                <>
                  <Button 
                    variant="default"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={enterMoveMode}
                  >
                    ↔️ Move Players
                  </Button>
                  <Button 
                    variant="default"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={exportTeeSheet}
                  >
                    📊 Export Tee Sheet
                  </Button>
                </>
              )}
              {user && user.role === 'admin' && isMoveMode && (
                <>
                  <Button 
                    variant="default"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={applyMoves}
                    disabled={applyingMoves || pendingMoves.length === 0}
                  >
                    {applyingMoves ? '⏳ Applying...' : `✅ Apply Changes (${pendingMoves.length})`}
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-orange-500 text-orange-600 hover:bg-orange-50"
                    onClick={undoLastMove}
                    disabled={moveHistory.length === 0}
                  >
                    ↩️ Undo
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                    onClick={exitMoveMode}
                  >
                    ❌ Cancel
                  </Button>
                </>
              )}
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
                disabled={isMoveMode}
              >
                ← Back to Dashboard
              </Button>
            </div>
          </div>
          
          {/* Move Mode Instructions */}
          {isMoveMode && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">🔄 Move Mode Active</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Drag players to empty slots or swap with other players</li>
                <li>• Use Undo to reverse moves before applying</li>
                <li>• Click Apply Changes to save all moves to the server</li>
                <li>• Click Cancel to discard all changes</li>
              </ul>
              {pendingMoves.length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm font-medium text-blue-800">Pending Moves ({pendingMoves.length}):</p>
                  <ul className="text-xs text-blue-600 mt-1">
                    {pendingMoves.map((move, idx) => (
                      <li key={idx}>• {move.playerName}: moved to new slot</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <div className="flex flex-wrap gap-4 mt-4">
            <Badge className="bg-emerald-100 text-emerald-800 px-3 py-1">
              Member Fee: R{event.member_fee}
            </Badge>
            <Badge className="bg-blue-100 text-blue-800 px-3 py-1">
              Non-member Fee: R{event.non_member_fee}
            </Badge>
            <Badge className="bg-purple-100 text-purple-800 px-3 py-1">
              Cart Fee: R{event.cart_fee}
            </Badge>
            {event.cart_required && (
              <Badge className="bg-red-100 text-red-800 px-3 py-1">
                Cart Required
              </Badge>
            )}
            {!event.free_rounds_allowed && (
              <Badge className="bg-gray-100 text-gray-800 px-3 py-1">
                Free Rounds Not Allowed
              </Badge>
            )}
          </div>
        </div>

        {/* Orphaned Bookings Alert (Admin only) */}
        {user?.role === 'admin' && orphanedBookings.length > 0 && (
          <Alert className="mb-6 border-amber-400 bg-amber-50">
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-semibold text-amber-800">
                      {orphanedBookings.length} Orphaned Booking{orphanedBookings.length > 1 ? 's' : ''} Found
                    </p>
                    <p className="text-sm text-amber-700">
                      These bookings don't match current tee time slots (likely due to interval or tee changes)
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-500 text-amber-700 hover:bg-amber-100"
                  onClick={() => setShowOrphanedModal(true)}
                >
                  View & Reassign
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Tee Sheet */}
        {isMoveMode ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="space-y-8">
              {event?.shotgun_start ? (
                renderTeeSection('Shotgun', getShotgunCombinedSlots(), true)
              ) : (
                Object.entries(teeTimeslots).map(([teeName, slots]) =>
                  renderTeeSection(teeName, slots)
                )
              )}
              
              {Object.keys(teeTimeslots).length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-600">No tee times available for this event.</p>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Drag Overlay for visual feedback */}
            <DragOverlay>
              {activeBooking ? (
                <div className="p-3 bg-blue-200 border-2 border-blue-500 rounded shadow-lg text-center">
                  <div className="text-sm font-medium text-blue-900">
                    {activeBooking.first_name} {activeBooking.last_name}
                  </div>
                  <div className="text-xs text-blue-700">Moving...</div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <div className="space-y-8">
            {event?.shotgun_start ? (
              renderTeeSection('Shotgun', getShotgunCombinedSlots(), true)
            ) : (
              Object.entries(teeTimeslots).map(([teeName, slots]) =>
                renderTeeSection(teeName, slots)
              )
            )}
            
            {Object.keys(teeTimeslots).length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-gray-600">No tee times available for this event.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Booking Modal */}
        <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle>Book Tee Time</DialogTitle>
            </DialogHeader>
            
            {selectedSlot && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-medium">{event?.shotgun_start ? 'Selected Hole' : 'Selected Time'}</p>
                  <p className="text-sm text-gray-600">
                    {event?.shotgun_start ? (
                      <>
                        {`Hole ${selectedSlot.teeTime.hole_number ?? (selectedSlot.teeTime.tee_name || '').replace(/[^0-9]/g, '')}`}
                        {selectedSlot.teeTime.time && (
                          <> · Shotgun start at {new Date(selectedSlot.teeTime.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false })}</>
                        )}
                      </>
                    ) : (
                      new Date(selectedSlot.teeTime.time).toLocaleString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })
                    )}
                  </p>
                </div>

                {/* Booking for other users (All users) */}
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Checkbox
                      id="booking-for-other"
                      checked={bookingData.booking_for_other}
                      onCheckedChange={(checked) => 
                        setBookingData({...bookingData, booking_for_other: checked, other_user_id: '', payment_responsibility: 'booker'})
                      }
                    />
                    <Label htmlFor="booking-for-other" className="text-sm">
                      Book for another user
                    </Label>
                  </div>
                  
                  {bookingData.booking_for_other && (
                    <div className="space-y-3">
                      {/* Searchable User Dropdown */}
                      <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={userSearchOpen}
                            className="w-full justify-between font-normal"
                          >
                            {bookingData.other_user_id
                              ? users.find((user) => user.id === bookingData.other_user_id)
                                ? `${users.find((user) => user.id === bookingData.other_user_id).first_name} ${users.find((user) => user.id === bookingData.other_user_id).last_name}`
                                : "Select user to book for"
                              : "Select user to book for"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search users..." />
                            <CommandList>
                              <CommandEmpty>No user found.</CommandEmpty>
                              <CommandGroup>
                                {users.map((user) => (
                                  <CommandItem
                                    key={user.id}
                                    value={`${user.first_name} ${user.last_name}`}
                                    onSelect={() => {
                                      setBookingData({...bookingData, other_user_id: user.id});
                                      setUserSearchOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${
                                        bookingData.other_user_id === user.id ? "opacity-100" : "opacity-0"
                                      }`}
                                    />
                                    {user.first_name} {user.last_name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      
                      {/* Payment Responsibility */}
                      <div>
                        <Label className="text-sm font-medium">Who will pay for this booking?</Label>
                        <Select
                          value={bookingData.payment_responsibility || 'booker'}
                          onValueChange={(value) =>
                            setBookingData({...bookingData, payment_responsibility: value})
                          }
                        >
                          <SelectTrigger data-testid="payment-responsibility-trigger">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="booker">
                              I will pay now
                            </SelectItem>
                            <SelectItem value="bookee">
                              {bookingData.other_user_id ?
                                `${users.find(u => u.id === bookingData.other_user_id)?.first_name || 'They'} will pay later (24hr deadline)` :
                                'They will pay later (24hr deadline)'
                              }
                            </SelectItem>
                            {user?.role === 'admin' && (
                              <SelectItem value="lost_booking" data-testid="payment-option-lost-booking">
                                Admin: Add lost booking
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {bookingData.payment_responsibility === 'bookee' && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <p className="text-sm text-yellow-800">
                            <strong>Note:</strong> The user will be notified and must pay within 24 hours, or the booking will be automatically cancelled.
                          </p>
                        </div>
                      )}

                      {bookingData.payment_responsibility === 'lost_booking' && (
                        <div className="bg-amber-50 border border-amber-300 rounded p-3 space-y-3" data-testid="lost-booking-panel">
                          <p className="text-sm text-amber-900">
                            <strong>Recovery mode:</strong> Creates a confirmed booking without charging anything. Use this when a user paid (e.g. on PayFast) but the booking did not appear in the system. Credits and free rounds are <strong>not</strong> deducted — adjust them manually if needed.
                          </p>

                          <div>
                            <Label className="text-sm font-medium">Original payment method</Label>
                            <Select
                              value={bookingData.lost_booking_payment_method}
                              onValueChange={(value) =>
                                setBookingData({...bookingData, lost_booking_payment_method: value})
                              }
                            >
                              <SelectTrigger data-testid="lost-booking-payment-method">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="card">Card (PayFast)</SelectItem>
                                <SelectItem value="credit">Credits</SelectItem>
                                <SelectItem value="free_round">Free round</SelectItem>
                                <SelectItem value="eft_other">EFT / Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label className="text-sm font-medium">Amount paid (R)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="e.g. 550"
                              value={bookingData.lost_booking_amount}
                              onChange={(e) => setBookingData({...bookingData, lost_booking_amount: e.target.value})}
                              data-testid="lost-booking-amount"
                            />
                          </div>

                          {bookingData.lost_booking_payment_method === 'card' && (
                            <div>
                              <Label className="text-sm font-medium">PayFast reference (optional)</Label>
                              <Input
                                placeholder="e.g. PF pf_payment_id"
                                value={bookingData.lost_booking_payfast_ref}
                                onChange={(e) => setBookingData({...bookingData, lost_booking_payfast_ref: e.target.value})}
                                data-testid="lost-booking-payfast-ref"
                              />
                            </div>
                          )}

                          <div>
                            <Label className="text-sm font-medium">Notes (optional)</Label>
                            <Input
                              placeholder="e.g. Browser closed before redirect on 2026-06-30"
                              value={bookingData.lost_booking_notes}
                              onChange={(e) => setBookingData({...bookingData, lost_booking_notes: e.target.value})}
                              data-testid="lost-booking-notes"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Multi-Booking Option - Pay for multiple players */}
                {!bookingData.booking_for_other && (
                <div className="border-t pt-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Checkbox
                      id="multi-booking"
                      checked={bookingData.multi_booking}
                      onCheckedChange={(checked) => {
                        setBookingData({
                          ...bookingData, 
                          multi_booking: checked, 
                          additional_user_ids: [],
                          multi_payment_method: 'card' // Default to card for multi-booking
                        });
                      }}
                    />
                    <Label htmlFor="multi-booking" className="text-sm">
                      Book & pay for additional players (group booking)
                    </Label>
                  </div>
                  
                  {bookingData.multi_booking && (
                    <div className="space-y-3 ml-6">
                      {/* Check if current user already has a booking */}
                      {(() => {
                        const userAlreadyBooked = Object.values(bookings).flat().some(
                          b => b.user_id === user?.id && b.status !== 'cancelled'
                        );
                        
                        return (
                          <>
                            {userAlreadyBooked ? (
                              <div className="bg-green-50 border border-green-200 rounded p-3">
                                <p className="text-sm text-green-800">
                                  ✓ You already have a booking for this event. 
                                </p>
                                <p className="text-xs text-green-700 mt-1">
                                  Only the players you add below will be booked.
                                </p>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500">
                                You and the players you add will be booked in consecutive slots.
                              </p>
                            )}
                          </>
                        );
                      })()}
                      
                      {/* Selected Users */}
                      {bookingData.additional_user_ids.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-600">Players to book:</p>
                          {bookingData.additional_user_ids.map((userId, index) => {
                            const selectedUser = users.find(u => u.id === userId);
                            return (
                              <div key={userId} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <span className="text-sm">
                                  {index + 1}. {selectedUser?.first_name} {selectedUser?.last_name}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setBookingData({
                                      ...bookingData,
                                      additional_user_ids: bookingData.additional_user_ids.filter(id => id !== userId)
                                    });
                                  }}
                                >
                                  ✕
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Add User Button */}
                      {bookingData.additional_user_ids.length < 3 && (
                        <Popover open={multiUserSearchOpen} onOpenChange={setMultiUserSearchOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full">
                              + Add Player ({3 - bookingData.additional_user_ids.length} remaining)
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search users..." />
                              <CommandList>
                                <CommandEmpty>No user found.</CommandEmpty>
                                <CommandGroup>
                                  {users
                                    .filter(u => u.id !== user.id && !bookingData.additional_user_ids.includes(u.id))
                                    .map((u) => (
                                    <CommandItem
                                      key={u.id}
                                      value={`${u.first_name} ${u.last_name}`}
                                      onSelect={() => {
                                        setBookingData({
                                          ...bookingData,
                                          additional_user_ids: [...bookingData.additional_user_ids, u.id]
                                        });
                                        setMultiUserSearchOpen(false);
                                      }}
                                    >
                                      {u.first_name} {u.last_name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                      
                      {/* Payment Method for Multi-Booking */}
                      {bookingData.additional_user_ids.length > 0 && (
                        <div className="mt-3">
                          <Label className="text-sm font-medium">Payment Method for Group</Label>
                          <Select
                            value={bookingData.multi_payment_method || 'card'}
                            onValueChange={(value) => {
                              setBookingData({...bookingData, multi_payment_method: value});
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Free Round Option - only if user has enough free rounds */}
                              {event.free_rounds_allowed && (user?.free_rounds || 0) >= bookingData.additional_user_ids.length && (
                                <SelectItem value="free_round">
                                  Use Free Rounds ({user?.free_rounds || 0} available)
                                </SelectItem>
                              )}
                              {/* Credits Option - only if user has credits */}
                              {(user?.credits_balance || 0) > 0 && (
                                <SelectItem value="credit">
                                  Use Credits (R{(user?.credits_balance || 0).toFixed(2)} available)
                                </SelectItem>
                              )}
                              {/* Card Option - always available */}
                              <SelectItem value="card">Pay by Card (PayFast)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">
                            {bookingData.multi_payment_method === 'free_round' 
                              ? `Will use ${bookingData.additional_user_ids.length} free round(s) from your balance`
                              : bookingData.multi_payment_method === 'credit'
                                ? 'Credits will be deducted from your balance'
                                : 'Single card payment for all players'}
                          </p>
                        </div>
                      )}
                      
                      {/* Info Note */}
                      {bookingData.additional_user_ids.length > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-2">
                          <p className="text-xs text-blue-600">
                            Note: Refunds for individual cancellations will be credited to your account.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Enhanced Payment Method Selection - Hide when user will pay later OR multi-booking */}
                {!(bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') && !bookingData.multi_booking && (
                <div>
                  <Label className="text-sm font-medium">Green Fee Payment</Label>
                  <Select
                    value={bookingData.payment_method}
                    onValueChange={(value) => {
                      // Update payment method and free round usage in single state update
                      setBookingData(prev => ({
                        ...prev, 
                        payment_method: value,
                        use_free_round: value === 'free_round'
                      }));
                      // No manual calculateCost() call - useEffect will handle it
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Dynamic options based on user's available resources */}
                      {(() => {
                        // Determine which user's resources to check
                        const targetUser = bookingData.booking_for_other && bookingData.other_user_id ? 
                          users.find(u => u.id === bookingData.other_user_id) : user;
                        
                        // Determine who's paying and whose resources to show
                        const payingUser = (bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') ?
                          targetUser : user; // If bookee pays, use target user's resources; otherwise use current user's
                        
                        return (
                          <>
                            {/* Free Round Option - show if paying user has free rounds and event allows it */}
                            {event.free_rounds_allowed && (payingUser?.free_rounds || 0) > 0 && (
                              <SelectItem value="free_round">
                                Use Free Round (Available: {payingUser?.free_rounds || 0})
                              </SelectItem>
                            )}
                            
                            {/* Credits Option - show if paying user has credits */}
                            {(payingUser?.credits_balance || 0) > 0 && (
                              <SelectItem value="credit">
                                Use Credits (Available: R{(payingUser?.credits_balance || 0).toFixed(2)})
                              </SelectItem>
                            )}
                            
                            {/* Card Payment - always available */}
                            <SelectItem value="card">Card Payment</SelectItem>
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                )}

                {/* Cart Fee Payment (if cart fee exists - carts are compulsory) - Hide when user will pay later */}
                {event.cart_fee > 0 && !(bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') && (
                  <div>
                    <Label className="text-sm font-medium">Cart Fee Payment (Required)</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Cart Fee: R{event.cart_fee.toFixed(2)}
                    </p>
                    <Select
                      value={bookingData.cart_payment_method}
                      onValueChange={(value) => {
                        setBookingData(prev => ({...prev, cart_payment_method: value}));
                        // No manual calculateCost() call - useEffect will handle it
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          // Determine which user's credits to check for cart fee payment
                          const payingUser = bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee' ?
                            (users.find(u => u.id === bookingData.other_user_id) || user) : user;
                        
                          return (
                            <>
                              {/* Credits Option - show if paying user has credits */}
                              {(payingUser?.credits_balance || 0) > 0 && (
                                <SelectItem value="credit">
                                  Use Credits (Available: R{(payingUser?.credits_balance || 0).toFixed(2)})
                                </SelectItem>
                              )}
                              
                              {/* Card Payment - always available */}
                              <SelectItem value="card">Card Payment</SelectItem>
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Mhala Hole-in-One Competition Option */}
                {event.mhala_hole_in_one_enabled && !bookingData.multi_booking && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-testid="mhala-booking-section">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="mhala-hole-in-one"
                        checked={bookingData.mhala_hole_in_one}
                        onCheckedChange={(checked) => {
                          setBookingData({...bookingData, mhala_hole_in_one: checked});
                        }}
                        data-testid="mhala-checkbox"
                      />
                      <Label htmlFor="mhala-hole-in-one" className="text-sm text-yellow-800 cursor-pointer">
                        Enter Mhala Hole-in-One Competition (+R{event.mhala_hole_in_one_fee || 50})
                      </Label>
                    </div>
                    {/* Mhala Payment Method Selector - Shows when Mhala is selected */}
                    {bookingData.mhala_hole_in_one && (
                      <div className="mt-3 pt-3 border-t border-yellow-200">
                        <Label className="text-xs text-yellow-700 mb-1 block">Pay R{event.mhala_hole_in_one_fee || 50} with:</Label>
                        <Select
                          value={bookingData.mhala_payment_method}
                          onValueChange={(value) => setBookingData({...bookingData, mhala_payment_method: value})}
                        >
                          <SelectTrigger className="bg-white" data-testid="mhala-payment-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="card">💳 Card / PayFast</SelectItem>
                            <SelectItem value="credits">💰 Credits ({user?.credits_balance || 0} available)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}

                {/* Prize Giving Dinner — compulsory for players, partner optional */}
                {event.dinner_enabled && !bookingData.multi_booking && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3" data-testid="dinner-booking-section">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-purple-800">
                        🍽️ Prize Giving Dinner — included (+R{(event.dinner_player_fee || 0).toFixed(2)})
                      </span>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">All players attend the prize giving dinner for this event.</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <Checkbox
                        id="dinner-partner"
                        checked={bookingData.dinner_partner}
                        onCheckedChange={(checked) => {
                          setBookingData({...bookingData, dinner_partner: checked});
                        }}
                        data-testid="dinner-partner-checkbox"
                      />
                      <Label htmlFor="dinner-partner" className="text-sm text-purple-700 cursor-pointer">
                        Bringing a partner (+R{(event.dinner_partner_fee || 0).toFixed(2)})
                      </Label>
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs font-medium text-purple-800">Dinner Fee Payment</Label>
                      <Select
                        value={bookingData.dinner_payment_method}
                        onValueChange={(value) => {
                          setBookingData(prev => ({...prev, dinner_payment_method: value}));
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid="dinner-payment-method-select">
                          <SelectValue placeholder="Select payment method" />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            const payingUser = bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee' ?
                              (users.find(u => u.id === bookingData.other_user_id) || user) : user;
                            return (
                              <>
                                {(payingUser?.credits_balance || 0) > 0 && (
                                  <SelectItem value="credit" data-testid="dinner-pay-credit-option">
                                    Use Credits (Available: R{(payingUser?.credits_balance || 0).toFixed(2)})
                                  </SelectItem>
                                )}
                                <SelectItem value="card" data-testid="dinner-pay-card-option">Pay by Card</SelectItem>
                              </>
                            );
                          })()}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-purple-600 mt-1">If credits don't cover the full dinner fee, the balance goes to card.</p>
                    </div>
                  </div>
                )}

                {/* Prize Giving Dinner notice for group bookings */}
                {event.dinner_enabled && bookingData.multi_booking && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3" data-testid="dinner-multi-booking-section">
                    <span className="text-sm font-medium text-purple-800">
                      🍽️ Prize Giving Dinner — included for every player (+R{(event.dinner_player_fee || 0).toFixed(2)} each)
                    </span>
                    <p className="text-xs text-purple-600 mt-1">Players can add a partner afterwards from My Bookings.</p>
                  </div>
                )}

                {/* Mhala Hole-in-One for Multi-Booking */}
                {event.mhala_hole_in_one_enabled && bookingData.multi_booking && bookingData.additional_user_ids.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-testid="mhala-multi-booking-section">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-yellow-800">
                        🏆 Mhala Hole-in-One Competition (+R{event.mhala_hole_in_one_fee || 50} each)
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mt-2">
                      <Checkbox
                        id="mhala-hole-in-one-all"
                        checked={bookingData.mhala_hole_in_one_all_players}
                        onCheckedChange={(checked) => {
                          setBookingData({...bookingData, mhala_hole_in_one_all_players: checked});
                        }}
                        data-testid="mhala-all-players-checkbox"
                      />
                      <Label htmlFor="mhala-hole-in-one-all" className="text-sm text-yellow-700 cursor-pointer">
                        Enter all players
                      </Label>
                    </div>
                    {/* Mhala Payment Method Selector for Multi-Booking */}
                    {bookingData.mhala_hole_in_one_all_players && (
                      <div className="mt-3 pt-3 border-t border-yellow-200">
                        <Label className="text-xs text-yellow-700 mb-1 block">Pay Mhala fees with:</Label>
                        <Select
                          value={bookingData.mhala_payment_method}
                          onValueChange={(value) => setBookingData({...bookingData, mhala_payment_method: value})}
                        >
                          <SelectTrigger className="bg-white" data-testid="mhala-payment-select-multi">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="card">💳 Card / PayFast</SelectItem>
                            <SelectItem value="credits">💰 Credits ({user?.credits_balance || 0} available)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}

                {/* Cost Breakdown - Hide when user will pay later */}
                {cost && !(bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') && (
                  <div className="border-t pt-4 space-y-2">
                    {/* Multi-Booking Breakdown */}
                    {cost.isMultiBooking && cost.playerBreakdown && (
                      <>
                        <div className="text-sm font-medium text-blue-700 mb-2">
                          Group Booking - {cost.playerCount} Players
                        </div>
                        {cost.playerBreakdown.map((player, idx) => (
                          <div key={idx} className="flex justify-between text-sm text-gray-600">
                            <span>{idx + 1}. {player.name} ({player.isMember ? 'Member' : 'Non-Member'})</span>
                            <span>R{(player.fee + player.cartFee).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t my-2"></div>
                      </>
                    )}
                    
                    {/* Standard Breakdown */}
                    {!cost.isMultiBooking && (
                      <div className="flex justify-between text-sm">
                        <span>{cost.membershipType} Fee</span>
                        <span>R{cost.greenFeeTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {cost.isMultiBooking && (
                      <div className="flex justify-between text-sm">
                        <span>Total Green Fees ({cost.playerCount} players)</span>
                        <span>R{cost.greenFeeTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {cost.cartFeeTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Cart Fee{cost.isMultiBooking ? ` (${cost.playerCount} players)` : ''}</span>
                        <span>R{cost.cartFeeTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {cost.mhalaFeeTotal > 0 && (
                      <div className="flex justify-between text-sm text-yellow-700">
                        <span>🏆 Mhala Hole-in-One{cost.isMultiBooking ? ` (${cost.playerCount} players)` : ''}</span>
                        <span>R{cost.mhalaFeeTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {cost.dinnerFeeTotal > 0 && (
                      <div className="flex justify-between text-sm text-purple-700" data-testid="dinner-cost-line">
                        <span>🍽️ Prize Giving Dinner{cost.isMultiBooking ? ` (${cost.playerCount} players)` : (bookingData.dinner_partner ? ' (incl. partner)' : '')}</span>
                        <span>R{cost.dinnerFeeTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {cost.creditsUsed > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Credits Used</span>
                        <span>-R{cost.creditsUsed.toFixed(2)}</span>
                      </div>
                    )}
                    {cost.freeRoundDiscount > 0 && (
                      <div className="flex justify-between text-sm text-purple-600">
                        <span>Free Round Discount</span>
                        <span>-R{cost.freeRoundDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-2">
                      <span>Total Amount</span>
                      <span className={cost.isMultiBooking ? 'text-blue-700' : ''}>
                        R{cost.finalAmount.toFixed(2)}
                      </span>
                    </div>
                    {cost.isMultiBooking && (
                      <div className="text-xs text-gray-500 mt-1">
                        Single card payment for all {cost.playerCount} players
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowBookingModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  
                  {/* Add to Cart button - only show for simple bookings (not multi-booking or admin booking for others) */}
                  {!bookingData.multi_booking && !bookingData.booking_for_other && (
                    <Button
                      variant="outline"
                      className="flex-1 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                      onClick={handleAddToCart}
                      data-testid="add-to-cart-btn"
                    >
                      Add to Cart
                    </Button>
                  )}
                  
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleBooking}
                    data-testid="confirm-booking-btn"
                  >
                    {/* Multi-booking always shows "Book & Pay" (card only) */}
                    {bookingData.multi_booking && bookingData.additional_user_ids.length > 0
                      ? (() => {
                          // Check if current user already has a booking
                          const userAlreadyBooked = Object.values(bookings).flat().some(
                            b => b.user_id === user?.id && b.status !== 'cancelled'
                          );
                          const playerCount = userAlreadyBooked 
                            ? bookingData.additional_user_ids.length 
                            : 1 + bookingData.additional_user_ids.length;
                          return `Book & Pay for ${playerCount} Player${playerCount > 1 ? 's' : ''}`;
                        })()
                      : (bookingData.booking_for_other && bookingData.payment_responsibility === 'bookee') || cost?.finalAmount === 0 
                        ? 'Confirm Booking' 
                        : 'Book & Pay'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Modal for Pay Now functionality */}
        <PaymentModal
          isOpen={paymentModal.isOpen}
          onClose={() => setPaymentModal({ isOpen: false, booking: null })}
          booking={paymentModal.booking}
          onPaymentSuccess={() => {
            setPaymentModal({ isOpen: false, booking: null });
            fetchEventData(); // Refresh tee sheet after payment
            refreshUser(); // Refresh user data to update credits balance
          }}
        />

        {/* Guest Booking Modal (Admin Only) */}
        <Dialog open={showGuestBookingModal} onOpenChange={setShowGuestBookingModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Guest Booking</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Guest Name *</Label>
                <Input
                  value={guestBookingData.guest_name}
                  onChange={(e) => setGuestBookingData({...guestBookingData, guest_name: e.target.value})}
                  placeholder="Enter guest name"
                />
              </div>
              <div>
                <Label>Guest Phone (Optional)</Label>
                <Input
                  value={guestBookingData.guest_phone}
                  onChange={(e) => setGuestBookingData({...guestBookingData, guest_phone: e.target.value})}
                  placeholder="e.g., 0821234567"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="guest-cart"
                  checked={guestBookingData.cart_required}
                  onCheckedChange={(checked) => setGuestBookingData({...guestBookingData, cart_required: checked})}
                />
                <Label htmlFor="guest-cart">Cart Required</Label>
              </div>
              <div>
                <Label>Payment Status</Label>
                <Select
                  value={guestBookingData.payment_status}
                  onValueChange={(value) => setGuestBookingData({...guestBookingData, payment_status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending Payment</SelectItem>
                    <SelectItem value="paid_cash">Paid (Cash)</SelectItem>
                    <SelectItem value="complimentary">Complimentary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowGuestBookingModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleGuestBooking} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Create Guest Booking
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reserve Slot Modal (Admin Only) */}
        <Dialog open={showReserveSlotModal} onOpenChange={setShowReserveSlotModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reserve Slot</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Reserve this slot to hold it temporarily. You can later convert it to a guest booking or release it.
              </p>
              <div>
                <Label>Note (Optional)</Label>
                <Textarea
                  value={reserveSlotData.reserve_note}
                  onChange={(e) => setReserveSlotData({...reserveSlotData, reserve_note: e.target.value})}
                  placeholder="e.g., Holding for John's friend"
                  rows={3}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowReserveSlotModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleReserveSlot} className="flex-1 bg-yellow-600 hover:bg-yellow-700">
                  Reserve Slot
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Own Booking Confirmation Dialog */}
        <Dialog open={cancelModal.isOpen} onOpenChange={(open) => !cancelModal.loading && setCancelModal({ isOpen: open, booking: null, loading: false })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Cancel Booking</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-600">
                Are you sure you want to cancel this booking?
              </p>
              {cancelModal.booking && (
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <p><strong>Event:</strong> {event?.name}</p>
                  <p><strong>Time:</strong> {cancelModal.booking.tee_time ? new Date(cancelModal.booking.tee_time).toLocaleString() : 'N/A'}</p>
                  <p><strong>Reference:</strong> {cancelModal.booking.booking_reference}</p>
                </div>
              )}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>Note:</strong> Cancellations within 48 hours of the event may not be refunded.
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setCancelModal({ isOpen: false, booking: null, loading: false })}
                  className="flex-1"
                  disabled={cancelModal.loading}
                >
                  Keep Booking
                </Button>
                <Button
                  onClick={confirmCancelOwnBooking}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={cancelModal.loading}
                >
                  {cancelModal.loading ? 'Cancelling...' : 'Cancel Booking'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Orphaned Bookings Modal */}
        <Dialog open={showOrphanedModal} onOpenChange={setShowOrphanedModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>⚠️ Orphaned Bookings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                These bookings were made at tee times that no longer exist in the current schedule. 
                This can happen when tee time intervals or available tees are changed after bookings were made.
              </p>
              
              {orphanedBookings.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No orphaned bookings found</p>
              ) : (
                <div className="rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left font-medium">Player</th>
                        <th className="p-3 text-left font-medium">Original Time</th>
                        <th className="p-3 text-left font-medium">Tee</th>
                        <th className="p-3 text-left font-medium">Nearest Slot</th>
                        <th className="p-3 text-right font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanedBookings.map((booking, idx) => (
                        <tr key={booking.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-3">
                            <div className="font-medium">{booking.user_name}</div>
                            {booking.user_email && (
                              <div className="text-xs text-gray-500">{booking.user_email}</div>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="text-red-600 font-mono">
                              {new Date(booking.tee_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{booking.tee_name}</Badge>
                          </td>
                          <td className="p-3">
                            {booking.nearest_slot ? (
                              <div>
                                <span className="text-emerald-600 font-mono">
                                  {new Date(booking.nearest_slot.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className="text-xs text-gray-500 ml-2">
                                  ({booking.nearest_slot.time_diff_minutes} min diff, {booking.nearest_slot.available_slots?.length || 0} slots open)
                                </span>
                              </div>
                            ) : (
                              <span className="text-amber-600">All slots full</span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                              onClick={() => setReassignModal({ 
                                isOpen: true, 
                                booking: booking, 
                                loading: false,
                                selectedTime: booking.nearest_slot?.time || null,
                                selectedSlot: booking.nearest_slot?.available_slots?.[0] ?? 0
                              })}
                            >
                              Reassign
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              <div className="flex justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setShowOrphanedModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reassign Booking Confirmation Modal */}
        <Dialog open={reassignModal.isOpen} onOpenChange={(open) => !reassignModal.loading && setReassignModal({ isOpen: open, booking: null, loading: false, selectedTime: null, selectedSlot: 0 })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Reassign Booking</DialogTitle>
            </DialogHeader>
            {reassignModal.booking && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">{reassignModal.booking.user_name}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Current: <span className="text-red-600 font-mono">
                      {event?.shotgun_start 
                        ? reassignModal.booking.tee_name 
                        : new Date(reassignModal.booking.tee_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      }
                    </span>
                    {!event?.shotgun_start && (
                      <> on <Badge variant="outline" className="ml-1">{reassignModal.booking.tee_name}</Badge></>
                    )}
                  </p>
                </div>
                
                {/* Tee Time / Hole Selection */}
                <div className="space-y-2">
                  <Label>{event?.shotgun_start ? 'Select New Hole' : 'Select New Tee Time'}</Label>
                  <Select
                    value={reassignModal.selectedTime || ''}
                    onValueChange={(val) => {
                      const selected = availableTeeTimes.find(t => t.time === val && t.tee_name === (event?.shotgun_start ? val.split('|')[1] : reassignModal.booking?.tee_name));
                      // For shotgun, val contains "time|tee_name"
                      if (event?.shotgun_start) {
                        const selectedShotgun = availableTeeTimes.find(t => `${t.time}|${t.tee_name}` === val);
                        setReassignModal(prev => ({ 
                          ...prev, 
                          selectedTime: val,
                          selectedTeeName: selectedShotgun?.tee_name,
                          selectedSlot: selectedShotgun?.available_slots?.[0] ?? 0
                        }));
                      } else {
                        setReassignModal(prev => ({ 
                          ...prev, 
                          selectedTime: val,
                          selectedSlot: selected?.available_slots?.[0] ?? 0
                        }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={event?.shotgun_start ? "Select a hole" : "Select a tee time"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {event?.shotgun_start ? (
                        // Shotgun event - show hole names
                        availableTeeTimes
                          .filter(t => !t.is_full)
                          .sort((a, b) => {
                            // Sort by hole number
                            const holeA = parseInt(a.tee_name.replace(/\D/g, '')) || 0;
                            const holeB = parseInt(b.tee_name.replace(/\D/g, '')) || 0;
                            return holeA - holeB;
                          })
                          .map(t => (
                            <SelectItem key={`${t.time}|${t.tee_name}`} value={`${t.time}|${t.tee_name}`}>
                              {t.tee_name}
                              <span className="text-gray-500 ml-2">
                                ({t.available_slots.length} slot{t.available_slots.length !== 1 ? 's' : ''} available)
                              </span>
                            </SelectItem>
                          ))
                      ) : (
                        // Regular event - show times
                        availableTeeTimes
                          .filter(t => t.tee_name === reassignModal.booking.tee_name && !t.is_full)
                          .map(t => (
                            <SelectItem key={t.time} value={t.time}>
                              {new Date(t.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              <span className="text-gray-500 ml-2">
                                ({t.available_slots.length} slot{t.available_slots.length !== 1 ? 's' : ''} available)
                              </span>
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  
                  {/* Suggested hint */}
                  {reassignModal.booking.nearest_slot && !reassignModal.selectedTime && (
                    <p className="text-xs text-amber-600">
                      💡 Suggested: {event?.shotgun_start 
                        ? reassignModal.booking.nearest_slot.tee_name 
                        : new Date(reassignModal.booking.nearest_slot.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      }
                      {!event?.shotgun_start && ` (${reassignModal.booking.nearest_slot.time_diff_minutes} min from original)`}
                    </p>
                  )}
                </div>
                
                {/* Slot Position Selection */}
                {reassignModal.selectedTime && (
                  <div className="space-y-2">
                    <Label>Select Slot Position</Label>
                    <Select
                      value={reassignModal.selectedSlot?.toString() || '0'}
                      onValueChange={(val) => setReassignModal(prev => ({ ...prev, selectedSlot: parseInt(val) }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select slot position" />
                      </SelectTrigger>
                      <SelectContent>
                        {(event?.shotgun_start 
                          ? availableTeeTimes.find(t => `${t.time}|${t.tee_name}` === reassignModal.selectedTime)
                          : availableTeeTimes.find(t => t.time === reassignModal.selectedTime)
                        )?.available_slots.map(idx => (
                          <SelectItem key={idx} value={idx.toString()}>Slot {idx + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Preview */}
                {reassignModal.selectedTime && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm text-emerald-800">
                      <strong>New assignment:</strong> {event?.shotgun_start 
                        ? `${reassignModal.selectedTime.split('|')[1]}, Slot ${(reassignModal.selectedSlot || 0) + 1}`
                        : `${new Date(reassignModal.selectedTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} on ${reassignModal.booking.tee_name} tee, Slot ${(reassignModal.selectedSlot || 0) + 1}`
                      }
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => setReassignModal({ isOpen: false, booking: null, loading: false, selectedTime: null, selectedSlot: 0 })}
                    disabled={reassignModal.loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      if (event?.shotgun_start) {
                        // For shotgun, extract time and tee_name from the combined value
                        const [time, teeName] = reassignModal.selectedTime.split('|');
                        handleReassignBooking(
                          reassignModal.booking,
                          time,
                          teeName,
                          reassignModal.selectedSlot || 0
                        );
                      } else {
                        handleReassignBooking(
                          reassignModal.booking,
                          reassignModal.selectedTime,
                          reassignModal.booking.tee_name,
                          reassignModal.selectedSlot || 0
                        );
                      }
                    }}
                    disabled={reassignModal.loading || !reassignModal.selectedTime}
                  >
                    {reassignModal.loading ? 'Reassigning...' : 'Confirm Reassignment'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TeeSheetView;